import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithPhoneNumber, RecaptchaVerifier,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  verifyFirebase, checkUsername,
  login as loginAPI,
} from "../api";
import { useAuth } from "../context/AuthContext";

const COUNTRY_CODES = [
  { code: "+977", flag: "🇳🇵", name: "Nepal" },
  { code: "+1",   flag: "🇺🇸", name: "USA/Canada" },
  { code: "+44",  flag: "🇬🇧", name: "UK" },
  { code: "+61",  flag: "🇦🇺", name: "Australia" },
  { code: "+65",  flag: "🇸🇬", name: "Singapore" },
  { code: "+971", flag: "🇦🇪", name: "UAE" },
  { code: "+81",  flag: "🇯🇵", name: "Japan" },
  { code: "+49",  flag: "🇩🇪", name: "Germany" },
];

const s = {
  page: {
    minHeight: "100vh", background: "#0b0b0c",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "40px 20px",
  },
  card: {
    background: "#111113", border: "0.5px solid #252528", borderRadius: 18,
    padding: "36px 32px", width: "100%", maxWidth: 420,
  },
  logo: {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontSize: 22, fontWeight: 800, color: "#f2f2f4", letterSpacing: "-0.5px",
    textAlign: "center", marginBottom: 8,
  },
  tabs: {
    display: "flex", marginBottom: 28,
    borderRadius: 10, overflow: "hidden",
    border: "0.5px solid #252528", background: "#0d0d0f",
  },
  tab: {
    flex: 1, padding: "9px", textAlign: "center",
    fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "'Bricolage Grotesque', sans-serif",
    transition: "all 0.15s", border: "none",
    color: "#55555e", background: "transparent",
  },
  tabActive: {
    background: "#1a1a1e", color: "#f0f0f0",
  },
  heading: {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontSize: 20, fontWeight: 800, color: "#f0f0f0",
    marginBottom: 6, letterSpacing: "-0.3px",
  },
  sub: {
    fontSize: 12, color: "#55555e", marginBottom: 24,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  methodCard: {
    width: "100%", padding: "16px 20px", borderRadius: 12, marginBottom: 10,
    fontSize: 14, fontWeight: 700, cursor: "pointer", textAlign: "left",
    display: "flex", alignItems: "center", gap: 14,
    fontFamily: "'Bricolage Grotesque', sans-serif",
    transition: "border-color 0.15s", border: "0.5px solid #2a2a2e",
    background: "#1a1a1c", color: "#e0e0e0",
  },
  input: {
    width: "100%", background: "#161618", border: "0.5px solid #252528",
    borderRadius: 10, padding: "11px 14px", fontSize: 13, color: "#c0c0c0",
    fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none",
    boxSizing: "border-box", marginBottom: 10,
  },
  btn: {
    width: "100%", padding: "12px", borderRadius: 10, fontSize: 13,
    fontWeight: 700, border: "none", cursor: "pointer",
    fontFamily: "'Bricolage Grotesque', sans-serif", transition: "opacity 0.15s",
  },
  error: {
    fontSize: 12, color: "#FF5C35", marginBottom: 10,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  back: {
    background: "none", border: "none", color: "#55555e",
    fontSize: 12, cursor: "pointer", marginBottom: 20,
    fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0,
  },
  label: {
    fontSize: 11, color: "#55555e", marginBottom: 5, display: "block",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
};

function friendlyFirebaseError(e) {
  const code = (e?.code || "").toLowerCase();
  if (code.includes("invalid-phone") || code.includes("invalid-number"))
    return "Invalid phone number — check and try again";
  if (code.includes("too-many-requests"))
    return "Too many attempts — wait a few minutes and try again";
  if (code.includes("invalid-verification-code") || code.includes("invalid-code"))
    return "Incorrect code — try again";
  if (code.includes("code-expired") || code.includes("session-expired"))
    return "Code expired — request a new one";
  if (code.includes("quota-exceeded"))
    return "SMS limit reached — try email verification instead";
  if (code.includes("captcha"))
    return "Verification check failed — refresh the page and try again";
  if (code.includes("user-not-found"))
    return "No account found with that email";
  if (code.includes("invalid-email"))
    return "Please enter a valid email address";
  if (code.includes("network"))
    return "Network error — check your connection and try again";
  return "Something went wrong — please try again";
}

function pwStrength(pwd) {
  if (!pwd) return { score: 0, label: "", color: "#252528", pct: 0 };
  let sc = 0;
  if (pwd.length >= 8)          sc++;
  if (pwd.length >= 12)         sc++;
  if (/[A-Z]/.test(pwd))        sc++;
  if (/[0-9]/.test(pwd))        sc++;
  if (/[^A-Za-z0-9]/.test(pwd)) sc++;
  const map = [
    { label: "too short",   color: "#FF5C35", pct: 10  },
    { label: "weak",        color: "#FF5C35", pct: 25  },
    { label: "fair",        color: "#f5a623", pct: 50  },
    { label: "good",        color: "#f5a623", pct: 70  },
    { label: "strong",      color: "#4a9a6a", pct: 88  },
    { label: "very strong", color: "#4a9a6a", pct: 100 },
  ];
  return { score: sc, ...map[sc] };
}

function OtpBoxes({ value, onChange }) {
  const refs   = useRef([]);
  const chars  = (value + "      ").slice(0, 6).split("");

  function handleChange(i, raw) {
    const ch   = raw.replace(/\D/g, "").slice(-1);
    const next = [...chars];
    next[i]    = ch;
    onChange(next.join("").replace(/\s/g, ""));
    if (ch && i < 5) refs.current[i + 1]?.focus();
  }

  function handleKey(i, e) {
    if (e.key === "Backspace" && !chars[i].trim() && i > 0) {
      refs.current[i - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(text);
    refs.current[Math.min(text.length, 5)]?.focus();
  }

  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16, justifyContent: "center" }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={chars[i].trim()}
          autoFocus={i === 0}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          style={{
            width: 44, height: 52, textAlign: "center",
            fontSize: 22, fontWeight: 700, fontFamily: "monospace",
            background: "#161618",
            border: `0.5px solid ${chars[i].trim() ? "#FF5C35" : "#252528"}`,
            borderRadius: 10, color: "#f0f0f0", outline: "none",
            transition: "border-color 0.15s",
          }}
        />
      ))}
    </div>
  );
}

function Logo() {
  return (
    <div style={s.logo}>
      <span style={{ color: "#FF5C35", marginRight: 4 }}>●</span>
      Guff<span style={{ color: "#FF5C35" }}>Gaff</span>
    </div>
  );
}

function Tabs({ active, onJoin, onLogin }) {
  return (
    <div style={s.tabs}>
      <button
        style={{ ...s.tab, ...(active === "join"  ? s.tabActive : {}) }}
        onClick={onJoin}
      >join</button>
      <button
        style={{ ...s.tab, ...(active === "login" ? s.tabActive : {}) }}
        onClick={onLogin}
      >sign in</button>
    </div>
  );
}

export default function Auth() {
  const navigate  = useNavigate();
  const { login, isLoggedIn, completeRegistration } = useAuth();

  // mode: "join" | "login" | "forgot"
  const [mode,   setMode]   = useState("join");
  // step: 1=method  2=verify  3=profile  4=success
  const [step,   setStep]   = useState(1);
  const [method, setMethod] = useState(null);
  const [error,  setError]  = useState("");
  const [busy,   setBusy]   = useState(false);

  // ── phone ───────────────────────────────────────────────────────────────
  const [cc,        setCc]        = useState("+977");
  const [phone,     setPhone]     = useState("");
  const [otp,       setOtp]       = useState("");
  const [otpSent,   setOtpSent]   = useState(false);
  const [countdown, setCountdown] = useState(0);
  const confirmRef  = useRef(null);
  const recaptchaEl = useRef(null);
  const verifierRef = useRef(null);

  // ── email ───────────────────────────────────────────────────────────────
  const [email,     setEmail]     = useState("");
  const [emailSent, setEmailSent] = useState(false);

  // ── profile ─────────────────────────────────────────────────────────────
  const [username,   setUsername]   = useState("");
  const [password,   setPassword]   = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd,    setShowPwd]    = useState(false);
  const [usernameOk, setUsernameOk] = useState(null);
  const [fbUid,      setFbUid]      = useState("");
  const [verPhone,      setVerPhone]      = useState(null);
  const [verEmail,      setVerEmail]      = useState(null);
  const [verifiedToken, setVerifiedToken] = useState("");

  // ── login ────────────────────────────────────────────────────────────────
  const [loginField,   setLoginField]   = useState("");
  const [loginPwd,     setLoginPwd]     = useState("");
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  // ── forgot ───────────────────────────────────────────────────────────────
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent,  setForgotSent]  = useState(false);

  // ── redirect if already in ──────────────────────────────────────────────
  useEffect(() => {
    if (isLoggedIn) navigate("/");
  }, [isLoggedIn, navigate]);

  // ── countdown ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // ── username debounce ────────────────────────────────────────────────────
  useEffect(() => {
    setUsernameOk(null);
    if (!username || username.length < 3) return;
    const t = setTimeout(async () => {
      const res = await checkUsername(username.trim());
      setUsernameOk(res.available);
    }, 400);
    return () => clearTimeout(t);
  }, [username]);

  // ── step 4 auto-redirect ─────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 4) return;
    const t = setTimeout(() => navigate("/"), 1500);
    return () => clearTimeout(t);
  }, [step, navigate]);

  // ── helpers ──────────────────────────────────────────────────────────────
  function switchMode(m) {
    setMode(m); setError(""); setStep(1); setMethod(null);
    setOtpSent(false); setOtp(""); setEmailSent(false);
    setForgotSent(false); setVerifiedToken("");
  }

  // ── phone OTP ────────────────────────────────────────────────────────────
  async function sendOtp() {
    setError("");
    if (!phone.trim()) { setError("Enter your phone number"); return; }
    setBusy(true);
    try {
      if (!verifierRef.current) {
        verifierRef.current = new RecaptchaVerifier(auth, recaptchaEl.current, {
          size: "invisible",
        });
      }
      const fullPhone = `${cc}${phone.replace(/^0+/, "")}`;
      confirmRef.current = await signInWithPhoneNumber(auth, fullPhone, verifierRef.current);
      setOtpSent(true);
      setCountdown(45);
    } catch (e) {
      setError(friendlyFirebaseError(e));
      verifierRef.current = null;
    } finally {
      setBusy(false);
    }
  }

  async function resendOtp() {
    verifierRef.current = null;
    setOtp("");
    setOtpSent(false);
    await sendOtp();
  }

  async function verifyOtp() {
    setError("");
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const result  = await confirmRef.current.confirm(otp);
      const idToken = await result.user.getIdToken();
      const res     = await verifyFirebase(idToken);
      if (res.token) {
        login(res.token, res.user);
        navigate("/");
      } else if (res.needs_username) {
        setFbUid(res.firebase_uid);
        setVerPhone(`${cc}${phone.replace(/^0+/, "")}`);
        setStep(3);
      } else {
        setError(res.error || "Verification failed");
      }
    } catch (e) {
      setError(friendlyFirebaseError(e));
    } finally {
      setBusy(false);
    }
  }

  // ── email OTP ────────────────────────────────────────────────────────────
  async function sendEmailOtp() {
    setError("");
    if (!email.trim()) { setError("Enter your email address"); return; }
    setBusy(true);
    try {
      const res  = await fetch("https://gufgaff.com/api/auth/send-otp", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to send code");
        return;
      }
      setEmailSent(true);
      setCountdown(45);
    } catch {
      setError("Cannot connect to server");
    } finally {
      setBusy(false);
    }
  }

  async function verifyEmailOtp() {
    setError("");
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const res  = await fetch("https://gufgaff.com/api/auth/verify-otp", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim().toLowerCase(), code: otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Invalid code");
        return;
      }
      setVerEmail(data.email);
      setVerifiedToken(data.verified_token);
      setStep(3);
    } catch {
      setError("Cannot connect to server");
    } finally {
      setBusy(false);
    }
  }

  // ── create account ───────────────────────────────────────────────────────
  async function createAccount() {
    setError("");
    if (!username.trim())    { setError("Choose a username"); return; }
    if (username.length < 3) { setError("Username must be at least 3 characters"); return; }
    if (!usernameOk)         { setError("That username is taken — pick another"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirmPwd) { setError("Passwords don't match"); return; }
    setBusy(true);
    try {
      await completeRegistration(
        fbUid,            username.trim(), password,
        verEmail       || undefined,
        verPhone       || undefined,
        verifiedToken  || undefined,
      );
      setStep(4);
    } catch (e) {
      setError(e.message || "Failed to create account — try again");
    } finally {
      setBusy(false);
    }
  }

  // ── login ────────────────────────────────────────────────────────────────
  async function handleLogin() {
    setError("");
    if (!loginField.trim()) { setError("Enter your username or email"); return; }
    if (!loginPwd)          { setError("Enter your password"); return; }
    setBusy(true);
    try {
      const res = await loginAPI(loginField.trim(), loginPwd);
      if (res.token) {
        login(res.token, res.user);
        navigate("/");
      } else {
        setError(res.error || "Sign in failed");
      }
    } catch {
      setError("Something went wrong — try again");
    } finally {
      setBusy(false);
    }
  }

  // ── forgot password ──────────────────────────────────────────────────────
  async function handleForgotPassword() {
    setError("");
    if (!forgotEmail.trim()) { setError("Enter your email address"); return; }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, forgotEmail.trim());
      setForgotSent(true);
    } catch (e) {
      setError(friendlyFirebaseError(e));
    } finally {
      setBusy(false);
    }
  }

  const primaryBtn = {
    ...s.btn, background: "#FF5C35", color: "#fff",
    opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer",
  };

  // ════════════════════════════════════════════════════════════════════════
  // Step 4 — success
  // ════════════════════════════════════════════════════════════════════════
  if (step === 4) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: "center", padding: "48px 32px" }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>🎬</div>
          <div style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 24, fontWeight: 800, color: "#f0f0f0", marginBottom: 10,
          }}>
            Welcome, @{username}!
          </div>
          <div style={{ fontSize: 13, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            you're in — taking you home…
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Login mode
  // ════════════════════════════════════════════════════════════════════════
  if (mode === "login") {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <Tabs active="login" onJoin={() => switchMode("join")} onLogin={() => {}} />
          <div style={s.heading}>sign in to GuffGaff</div>
          <div style={s.sub}>welcome back</div>

          <label style={s.label}>username or email</label>
          <input
            style={s.input}
            type="text"
            placeholder="username or email"
            value={loginField}
            autoFocus
            onChange={e => setLoginField(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") document.getElementById("loginPwdInput")?.focus();
            }}
          />

          <label style={s.label}>password</label>
          <div style={{ position: "relative", marginBottom: 10 }}>
            <input
              id="loginPwdInput"
              style={{ ...s.input, marginBottom: 0, paddingRight: 44 }}
              type={showLoginPwd ? "text" : "password"}
              placeholder="your password"
              value={loginPwd}
              onChange={e => setLoginPwd(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
            />
            <button
              onClick={() => setShowLoginPwd(p => !p)}
              style={{
                position: "absolute", right: 12, top: "50%",
                transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer",
                color: "#55555e", fontSize: 14, padding: 2,
              }}
            >
              {showLoginPwd ? "🙈" : "👁"}
            </button>
          </div>

          {error && <div style={s.error}>{error}</div>}

          <button style={{ ...primaryBtn, marginTop: 4 }} disabled={busy} onClick={handleLogin}>
            {busy ? "signing in…" : "Sign in"}
          </button>

          <div style={{
            textAlign: "center", marginTop: 16, fontSize: 12,
            fontFamily: "'Plus Jakarta Sans', sans-serif", color: "#55555e",
            lineHeight: 2,
          }}>
            <span
              style={{ color: "#FF5C35", cursor: "pointer" }}
              onClick={() => { setMode("forgot"); setError(""); }}
            >
              Forgot password?
            </span>
            <br />
            <span style={{ color: "#55555e" }}>Don't have an account?{" "}</span>
            <span
              style={{ color: "#FF5C35", cursor: "pointer" }}
              onClick={() => switchMode("join")}
            >
              Join →
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Forgot password mode
  // ════════════════════════════════════════════════════════════════════════
  if (mode === "forgot") {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <button
            style={{ ...s.back, marginTop: 8 }}
            onClick={() => { setMode("login"); setError(""); setForgotSent(false); setForgotEmail(""); }}
          >
            ← back to sign in
          </button>
          <div style={s.heading}>reset your password</div>
          <div style={s.sub}>we'll send a reset link to your email</div>

          {!forgotSent ? (
            <>
              <label style={s.label}>email address</label>
              <input
                style={s.input}
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                autoFocus
                onChange={e => setForgotEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleForgotPassword()}
              />
              {error && <div style={s.error}>{error}</div>}
              <button style={primaryBtn} disabled={busy} onClick={handleForgotPassword}>
                {busy ? "sending…" : "Send reset link"}
              </button>
            </>
          ) : (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>📬</div>
              <div style={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 8,
              }}>
                check your email
              </div>
              <div style={{
                fontSize: 13, color: "#55555e", lineHeight: 1.6,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                We sent a reset link to{" "}
                <strong style={{ color: "#c0c0c0" }}>{forgotEmail}</strong>.
                Follow the instructions in the email.
              </div>
              <button
                onClick={() => { setForgotSent(false); setForgotEmail(""); setError(""); }}
                style={{ ...s.back, marginTop: 20 }}
              >
                try a different email
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Step 1 — choose method
  // ════════════════════════════════════════════════════════════════════════
  if (step === 1) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <Tabs active="join" onJoin={() => {}} onLogin={() => switchMode("login")} />
          <div style={s.heading}>join the guff</div>
          <div style={s.sub}>verify with phone or email to get started</div>

          <button
            style={s.methodCard}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#FF5C35"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#2a2a2e"; }}
            onClick={() => { setMethod("phone"); setStep(2); setError(""); }}
          >
            <span style={{ fontSize: 26 }}>📱</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Phone number</div>
              <div style={{ fontSize: 11, color: "#55555e", fontWeight: 400 }}>OTP sent via SMS</div>
            </div>
          </button>

          <button
            style={s.methodCard}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#FF5C35"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#2a2a2e"; }}
            onClick={() => { setMethod("email"); setStep(2); setError(""); }}
          >
            <span style={{ fontSize: 26 }}>📧</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Email address</div>
              <div style={{ fontSize: 11, color: "#55555e", fontWeight: 400 }}>sign-in link sent to inbox</div>
            </div>
          </button>

          {error && <div style={{ ...s.error, textAlign: "center", marginTop: 6 }}>{error}</div>}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Step 2A — phone verification
  // ════════════════════════════════════════════════════════════════════════
  if (step === 2 && method === "phone") {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <button
            style={s.back}
            onClick={() => { setStep(1); setError(""); setOtpSent(false); setOtp(""); }}
          >
            ← back
          </button>
          <div style={s.heading}>enter your number</div>
          <div style={s.sub}>we'll send a 6-digit code to verify</div>

          {!otpSent ? (
            <>
              <label style={s.label}>country</label>
              <select
                value={cc}
                onChange={e => setCc(e.target.value)}
                style={{ ...s.input, marginBottom: 8 }}
              >
                {COUNTRY_CODES.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.name} ({c.code})
                  </option>
                ))}
              </select>

              <label style={s.label}>phone number</label>
              <input
                style={s.input}
                type="tel"
                placeholder="98XXXXXXXX"
                value={phone}
                autoFocus
                onChange={e => setPhone(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendOtp()}
              />
              {error && <div style={s.error}>{error}</div>}
              <button style={primaryBtn} disabled={busy} onClick={sendOtp}>
                {busy ? "sending…" : "Send OTP"}
              </button>
            </>
          ) : (
            <>
              <div style={{
                fontSize: 13, color: "#8a8a94", marginBottom: 16,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                Code sent to {cc} {phone}
              </div>

              <OtpBoxes value={otp} onChange={setOtp} />

              <div style={{
                textAlign: "center", fontSize: 11, color: "#55555e",
                marginBottom: 14, fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                {countdown > 0
                  ? `Resend in ${countdown}s`
                  : <button onClick={resendOtp} style={{ ...s.back, fontSize: 11 }}>resend code</button>
                }
              </div>

              {error && <div style={s.error}>{error}</div>}
              <button style={primaryBtn} disabled={busy} onClick={verifyOtp}>
                {busy ? "verifying…" : "Verify OTP"}
              </button>
            </>
          )}

          <div ref={recaptchaEl} />
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Step 2B — email OTP verification
  // ════════════════════════════════════════════════════════════════════════
  if (step === 2 && method === "email") {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Logo />
          <button
            style={s.back}
            onClick={() => { setStep(1); setError(""); setEmailSent(false); setOtp(""); }}
          >
            ← back
          </button>
          <div style={s.heading}>enter your email</div>
          <div style={s.sub}>
            {!emailSent ? "we'll send a 6-digit code" : `code sent to ${email}`}
          </div>

          {!emailSent ? (
            <>
              <input
                style={s.input}
                type="email"
                placeholder="you@example.com"
                value={email}
                autoFocus
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendEmailOtp()}
              />
              {error && (
                <div style={s.error}>
                  {error}{" "}
                  {error.includes("already registered") && (
                    <span
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => switchMode("login")}
                    >
                      Sign in instead →
                    </span>
                  )}
                </div>
              )}
              <button style={primaryBtn} disabled={busy} onClick={sendEmailOtp}>
                {busy ? "sending…" : "Send code"}
              </button>
            </>
          ) : (
            <>
              <OtpBoxes value={otp} onChange={setOtp} />
              <div style={{
                textAlign: "center", fontSize: 11, color: "#55555e",
                marginBottom: 14, fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                {countdown > 0
                  ? `Resend in ${countdown}s`
                  : <button
                      onClick={() => { setEmailSent(false); setOtp(""); }}
                      style={{ ...s.back, fontSize: 11 }}
                    >resend code</button>
                }
              </div>
              {error && <div style={s.error}>{error}</div>}
              <button style={primaryBtn} disabled={busy} onClick={verifyEmailOtp}>
                {busy ? "verifying…" : "Verify code"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Step 3 — create profile
  // ════════════════════════════════════════════════════════════════════════
  const str = pwStrength(password);
  const canSubmit = usernameOk && password.length >= 8 && password === confirmPwd && !busy;

  return (
    <div style={s.page}>
      <div style={s.card}>
        <Logo />
        <div style={{ ...s.heading, marginTop: 8 }}>Welcome to GuffGaff 🎬</div>
        <div style={s.sub}>create your profile to join the conversation</div>

        {/* Username */}
        <label style={s.label}>username</label>
        <div style={{ position: "relative", marginBottom: 4 }}>
          <input
            style={{ ...s.input, marginBottom: 0, paddingRight: 36 }}
            type="text"
            placeholder="letters, numbers, underscores"
            value={username}
            autoFocus
            onChange={e =>
              setUsername(
                e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20)
              )
            }
          />
          {username.length >= 3 && usernameOk !== null && (
            <span style={{
              position: "absolute", right: 12, top: "50%",
              transform: "translateY(-50%)", fontSize: 14,
            }}>
              {usernameOk ? "✅" : "❌"}
            </span>
          )}
        </div>
        <div style={{ minHeight: 18, marginBottom: 6, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12 }}>
          {username.length > 0 && username.length < 3 && (
            <span style={{ color: "#FF5C35" }}>at least 3 characters</span>
          )}
          {username.length >= 3 && usernameOk === null && (
            <span style={{ color: "#55555e" }}>checking…</span>
          )}
          {username.length >= 3 && usernameOk === false && (
            <span style={{ color: "#FF5C35" }}>username taken</span>
          )}
          {username.length >= 3 && usernameOk === true && (
            <span style={{ color: "#4a9a6a" }}>username available</span>
          )}
        </div>

        {/* Password */}
        <label style={s.label}>password</label>
        <div style={{ position: "relative", marginBottom: 4 }}>
          <input
            style={{ ...s.input, marginBottom: 0, paddingRight: 44 }}
            type={showPwd ? "text" : "password"}
            placeholder="at least 8 characters"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button
            onClick={() => setShowPwd(p => !p)}
            style={{
              position: "absolute", right: 12, top: "50%",
              transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer",
              color: "#55555e", fontSize: 14, padding: 2,
            }}
          >
            {showPwd ? "🙈" : "👁"}
          </button>
        </div>

        {/* Strength bar */}
        {password.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 3, marginBottom: 3 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: i <= str.score ? str.color : "#1e1e22",
                  transition: "background 0.2s",
                }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: str.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {str.label}
            </div>
          </div>
        )}

        {/* Confirm password */}
        <label style={s.label}>confirm password</label>
        <input
          style={{
            ...s.input,
            borderColor: confirmPwd && confirmPwd !== password ? "#FF5C35" : undefined,
          }}
          type="password"
          placeholder="repeat password"
          value={confirmPwd}
          onChange={e => setConfirmPwd(e.target.value)}
          onKeyDown={e => e.key === "Enter" && createAccount()}
        />
        {confirmPwd && confirmPwd !== password && (
          <div style={{ ...s.error, marginTop: -6 }}>passwords don't match</div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <button
          style={{ ...primaryBtn, marginTop: 4, opacity: canSubmit ? 1 : 0.5 }}
          disabled={!canSubmit}
          onClick={createAccount}
        >
          {busy ? "creating account…" : "Create my account"}
        </button>
      </div>
    </div>
  );
}
